import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Target, Users, Shield, Rocket, Heart } from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip, useReveal } from "components/jhala/Layout.js";

function CountUp({ end, suffix = "", duration = 1400 }) {
  const [ref, visible] = useReveal();
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(progress * end));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* Simple abstract "team" illustration — circles + connecting lines, gently animated.
   Not a photo, but adds visual warmth/friendliness to the About page. */
function TeamIllustration() {
  const people = [
    { x: 60, y: 60, r: 20, color: "#4CE0B3" },
    { x: 140, y: 40, r: 16, color: "#FF5C39" },
    { x: 130, y: 100, r: 18, color: "#8B93A7" },
    { x: 200, y: 75, r: 14, color: "#4CE0B3" },
  ];
  return (
    <svg viewBox="0 0 240 140" style={{ width: "100%", height: 110 }}>
      <line x1="60" y1="60" x2="140" y2="40" stroke="#23304A" strokeWidth="1.5" />
      <line x1="60" y1="60" x2="130" y2="100" stroke="#23304A" strokeWidth="1.5" />
      <line x1="130" y1="100" x2="200" y2="75" stroke="#23304A" strokeWidth="1.5" />
      <line x1="140" y1="40" x2="200" y2="75" stroke="#23304A" strokeWidth="1.5" />
      {people.map((p, i) => (
        <circle
          key={i}
          cx={p.x} cy={p.y} r={p.r}
          fill={p.color}
          opacity={0.85}
          style={{
            animation: `${i % 2 === 0 ? "blobFloat" : "blobFloat2"} ${4 + i}s ease-in-out infinite`,
            transformOrigin: `${p.x}px ${p.y}px`,
          }}
        />
      ))}
    </svg>
  );
}

const VALUES = [
  { icon: Shield, title: "Craft over shortcuts", desc: "Clean code and thoughtful design — not just \"it works.\"" },
  { icon: Users, title: "Communication first", desc: "You always know exactly where your project stands." },
  { icon: Rocket, title: "Built to last", desc: "We build for where you're going, not just where you are today." },
  { icon: Heart, title: "Partnership, not transactions", desc: "We aim for long-term relationships, not one-off gigs." },
];

export default function AboutUsPage() {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header active="About" />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ position: "absolute", top: -40, right: 60, width: 180, height: 180, borderRadius: "50%", background: "#4CE0B3", opacity: 0.14, filter: "blur(30px)", animation: "blobFloat 7s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: -50, right: 220, width: 140, height: 140, borderRadius: "50%", background: "#FF5C39", opacity: 0.12, filter: "blur(30px)", animation: "blobFloat2 8s ease-in-out infinite" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --about</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <span className="wave-hand" style={{ fontSize: "0.85em" }}>👋</span>
              Innovate. Integrate. Elevate.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 580, lineHeight: 1.65 }}>
              JhalaTech is an IT services company built on one idea: businesses don't just need a developer —
              they need a partner who understands the whole picture, from the storefront to the server room.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUR STORY */}
      <section style={{ padding: "80px 24px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48 }} className="story-grid">
          <Reveal>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>01 / our story</span>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "10px 0 18px" }}>
              Why we started JhalaTech
            </h2>
            <p style={{ color: "#4A5468", fontSize: 15, lineHeight: 1.75, marginBottom: 16 }}>
              JhalaTech started with a simple observation: most agencies pick a lane. Either they're a
              Shopify shop, or they're a dev shop — rarely both. That gap meant clients constantly had to
              juggle multiple vendors just to get one product working end to end.
            </p>
            <p style={{ color: "#4A5468", fontSize: 15, lineHeight: 1.75 }}>
              We built our team to close that gap — specialists in Shopify and ecommerce, backed by
              full-stack engineers and DevOps expertise, so nothing breaks after launch and nothing
              is out of reach when a client needs more than a template.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div style={{ background: "#0A0F1E", borderRadius: 10, padding: "32px 28px", height: "100%" }}>
              <TeamIllustration />
              <div style={{ display: "flex", flexDirection: "column", gap: 22, marginTop: 12 }}>
                <div style={{ borderBottom: "1px solid #23304A", paddingBottom: 18 }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#4CE0B3", fontSize: 18 }}>Shopify + Full-Stack</div>
                  <div style={{ color: "#8B93A7", fontSize: 13, marginTop: 4 }}>one team, both specialties</div>
                </div>
                <div style={{ borderBottom: "1px solid #23304A", paddingBottom: 18 }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#4CE0B3", fontSize: 18, animation: "pulseSoft 2.5s ease-in-out infinite" }}>24/7</div>
                  <div style={{ color: "#8B93A7", fontSize: 13, marginTop: 4 }}>server & DevOps coverage</div>
                </div>
                <div style={{ borderBottom: "1px solid #23304A", paddingBottom: 18 }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#4CE0B3", fontSize: 18 }}>
                    <CountUp end={100} suffix="%" />
                  </div>
                  <div style={{ color: "#8B93A7", fontSize: 13, marginTop: 4 }}>clients get post-launch support</div>
                </div>
                <p style={{ color: "#B8BFCF", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                  We're a growing team — as we take on more Shopify and full-stack projects, this is where
                  we'll add real client numbers and milestones.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section style={{ padding: "0 24px 88px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>02 / what's different</span>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "10px 0 18px" }}>
              Not just a Shopify shop. Not just a dev shop.
            </h2>
            <p style={{ color: "#4A5468", fontSize: 15, lineHeight: 1.75, maxWidth: 720 }}>
              When a client needs a custom checkout feature Shopify can't do natively, we don't say no —
              we build it. When a store needs to scale beyond ecommerce into a full platform, we're already
              equipped to take it there. That's the difference one team, full-stack, makes.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section style={{ padding: "0 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>03 / our values</span>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "10px 0 36px" }}>
              How we work
            </h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 18 }}>
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="value-card" style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "26px 22px", background: "#FFFFFF", height: "100%" }}>
                  <v.icon size={22} className="icon-pop" style={{ color: "#FF5C39", marginBottom: 14 }} />
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, margin: "0 0 8px" }}>{v.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center" }}>
        <Reveal>
          <Target size={26} style={{ color: "#4CE0B3", marginBottom: 16 }} />
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Want to work with us?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            Tell us about your project — we'd love to hear what you're building.
          </p>
          <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-block" }}>
            Get In Touch
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
